import { LocalNotifications } from '@capacitor/local-notifications';
import { Expense } from './supabaseService';

// Hash code helper to turn string IDs (UUIDs or timestamps) into unique 32-bit integers
const getNumericId = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const character = str.charCodeAt(i);
    hash = (hash << 5) - hash + character;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % 100000000;
};

/**
 * Request system permissions for push/local notifications
 */
export const requestNotificationPermissions = async (): Promise<boolean> => {
  try {
    const permission = await LocalNotifications.requestPermissions();
    return permission.display === 'granted';
  } catch (err) {
    console.error("Error requesting notifications permission:", err);
    return false;
  }
};

/**
 * Check if notifications permissions are granted
 */
export const checkNotificationPermissions = async (): Promise<boolean> => {
  try {
    const status = await LocalNotifications.checkPermissions();
    return status.display === 'granted';
  } catch (err) {
    console.error("Error checking notifications permission:", err);
    return false;
  }
};

/**
 * Schedule a native test notification in 5 seconds
 */
export const scheduleTestNotification = async (): Promise<boolean> => {
  const hasPermission = await checkNotificationPermissions();
  if (!hasPermission) {
    const granted = await requestNotificationPermissions();
    if (!granted) return false;
  }

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: 9999, // Unique test ID
          title: "Zeyo✧ Test Réussi !",
          body: "Les notifications iOS de Zeyo sont maintenant actives et opérationnelles.",
          schedule: { at: new Date(Date.now() + 5000) }, // 5 seconds from now
          sound: undefined,
          actionTypeId: "",
          extra: null
        }
      ]
    });
    return true;
  } catch (err) {
    console.error("Failed to schedule test notification:", err);
    return false;
  }
};

/**
 * Auto-schedule local notifications for all upcoming monthly bills
 */
export const scheduleBillReminders = async (expenses: Expense[], enabled: boolean = true) => {
  try {
    const hasPermission = await checkNotificationPermissions();
    if (!hasPermission || !enabled) {
      // Cancel pending reminders (excluding test notifications)
      const pending = await LocalNotifications.getPending();
      const reminders = pending.notifications.filter(n => n.id !== 9999);
      if (reminders.length > 0) {
        await LocalNotifications.cancel({ notifications: reminders });
      }
      return;
    }

    // 1. Cancel all previously scheduled notifications to avoid duplicates
    const pending = await LocalNotifications.getPending();
    const remindersToCancel = pending.notifications.filter(n => n.id !== 9999);
    if (remindersToCancel.length > 0) {
      await LocalNotifications.cancel({ notifications: remindersToCancel });
    }

    // 2. Filter unpaid monthly bills
    const unpaidBills = expenses.filter(
      e => (e.category === 'mensuel' || e.category?.startsWith('mensuel')) && !e.is_paid
    );

    const now = new Date();
    const notificationsToSchedule = [];

    for (const bill of unpaidBills) {
      if (!bill.due_date) continue;

      // Parse due date. Support both YYYY-MM-DD or standard date format
      const dueDate = new Date(bill.due_date);
      if (isNaN(dueDate.getTime())) continue;

      // Set reminder time to 1 day before the due date, at 9:00 AM
      const reminderDate = new Date(dueDate);
      reminderDate.setDate(dueDate.getDate() - 1);
      reminderDate.setHours(9, 0, 0, 0);

      // If the reminder date is in the past, schedule it for 2 hours from now if the due date is still in the future
      if (reminderDate.getTime() <= now.getTime()) {
        if (dueDate.getTime() > now.getTime()) {
          // Alert today or in 2 hours
          reminderDate.setTime(now.getTime() + 2 * 60 * 60 * 1000);
        } else {
          // Due date is already in the past, skip
          continue;
        }
      }

      notificationsToSchedule.push({
        id: getNumericId(bill.id),
        title: "Prélèvement imminent 💸",
        body: `Rappel : Le prélèvement "${bill.title}" de ${bill.amount.toFixed(2)} € est prévu demain.`,
        schedule: { at: reminderDate },
        sound: undefined,
        actionTypeId: "",
        extra: null
      });
    }

    if (notificationsToSchedule.length > 0) {
      await LocalNotifications.schedule({
        notifications: notificationsToSchedule
      });
      console.log(`[Notifications] Scheduled ${notificationsToSchedule.length} reminders.`);
    }
  } catch (err) {
    console.error("Error scheduling bill reminders:", err);
  }
};

/**
 * Trigger an instant native notification (fires in 500ms)
 */
export const triggerInstantNotification = async (title: string, body: string) => {
  try {
    const hasPermission = await checkNotificationPermissions();
    if (!hasPermission) return;

    await LocalNotifications.schedule({
      notifications: [
        {
          id: Math.floor(Math.random() * 1000000), // Random unique ID
          title,
          body,
          schedule: { at: new Date(Date.now() + 500) }, // 500ms from now
          sound: undefined,
          actionTypeId: "",
          extra: null
        }
      ]
    });
  } catch (err) {
    console.error("Error triggering instant notification:", err);
  }
};
